import { client, gql } from 'data-provider';

export interface AppInstance {
  id: string;
  name: string;
  displayName: string;
  description: string;
}

export interface ResponseMessage {
  message?: string;
}

export type CreateAppInstanceResponse = AppInstance & ResponseMessage;

export interface CreateDemoResponse extends ResponseMessage {
  organizationId: string;
  instansId: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  displayName: string;
}

export const getAppInstances = async () => {
  const query = gql`
    query GetAllInstans {
      getAllInstans {
        id
        name
        display_name
        description
      }
    }
  `;

  const data = await client.request(query);
  return data.getAllInstans.map(
    (org: Omit<AppInstance, 'displayName'> & { display_name: string }) => {
      const { display_name, ...rest } = org;
      return { ...rest, displayName: display_name };
    }
  ) as AppInstance[];
};

export const getServiceCategories = async () => {
  const query = gql`
    query GetServiceCategories {
      getServiceCategories {
        id
        name
        display_name
      }
    }
  `;

  const data = await client.request(query);
  return data.getServiceCategories.map(
    (category: Omit<ServiceCategory, 'displayName'> & { display_name: string }) => {
      const { display_name, ...rest } = category;
      return { ...rest, displayName: display_name };
    }
  ) as ServiceCategory[];
};

export const createAppInstance = async (
  name: string,
  description: string,
  categoryId: string,
  seedData?: boolean
) => {
  const mutation = gql`
    mutation ($input: CreateInstansInput!) {
      createInstans(input: $input) {
        id
        name
        display_name
        description
        message
      }
    }
  `;

  const variables = {
    input: {
      data: { name, description, category_id: categoryId },
      ...(seedData ? { seed_data: true } : {}),
    },
  };

  const createdData = await client.request(mutation, variables);
  const payload = createdData.createInstans as {
    id: string;
    name: string;
    display_name: string;
    description: string;
    message?: string;
  };
  const { display_name, message, ...rest } = payload;
  return {
    ...rest,
    displayName: display_name,
    ...(message ? { message } : {}),
  } as CreateAppInstanceResponse;
};

export const updateAppInstance = async (
  appInstanceId: string,
  data: { name?: string; description?: string }
) => {
  const mutation = gql`
    mutation ($input: UpdateInstansInput!) {
      updateInstans(input: $input) {
        instans {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id: appInstanceId },
      data,
    },
  };

  const updatedData = await client.request(mutation, variables);
  const payload = updatedData.updateInstans as { instans: { id: string } };
  return { appInstance: { id: payload.instans.id } };
};

export const deleteAppInstance = async (appInstanceId: string) => {
  const mutation = gql`
    mutation ($input: DeleteInstansInput!) {
      deleteInstans(input: $input) {
        instans {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id: appInstanceId },
    },
  };

  await client.request(mutation, variables);
};

export const createDemo = async (
  orgId: string | undefined,
  categoryId?: string
) => {
  const mutation = gql`
    mutation ($input: CreateDemoInput!){
      createDemo(input: $input) {
        org_id
        instans_id
        message
      }
    }
  `;
  const data: { organization_id: string | undefined; category_id?: string } = {
    organization_id: orgId,
  };
  if (categoryId) {
    data.category_id = categoryId;
  }

  const variables = {
    input: { data },
  };

  const response = await client.request(mutation, variables);
  const payload = response.createDemo as {
    org_id: string;
    instans_id: string;
    message?: string;
  };
  return {
    organizationId: payload.org_id,
    instansId: payload.instans_id,
    ...(payload.message ? { message: payload.message } : {}),
  } as CreateDemoResponse;
};
